import { type ComponentProps, memo, type MouseEvent, useMemo } from 'react'

import { type RecType } from '@/defs/rec'
import { type InViewStatus } from '@/hooks/useInView'

import { CardFaces } from '../../CardFaces/CardFaces'
import { useCardFaceProps } from '../../hooks/useCardFaceProps'

type CardFacesProps = ComponentProps<typeof CardFaces>

type Props = {
  animate?: boolean
  inViewStatus?: InViewStatus
  linkProps?: {
    href: string
    newTab?: boolean
  }
  onClick?: (e: MouseEvent<HTMLElement>) => void
  rec: RecType
  showPrices?: boolean
}

const CardRecFacesInner = ({ animate, inViewStatus = 'inView', linkProps, onClick, rec, showPrices }: Props) => {
  const enabled = inViewStatus !== 'outside'
  const { cardId, name, onlyBack, onlyFront, onlyPartner, partnerRef, reverse, scryfallId } = rec
  const { back, front } = useCardFaceProps({ cardId, enabled, name, scryfallId })
  const { front: frontPartner } = useCardFaceProps({
    cardId: partnerRef?.cardId,
    enabled: enabled && !!partnerRef,
    scryfallId: partnerRef?.scryfallId,
  })

  const facesProps = useMemo<CardFacesProps>(() => {
    const isPartner = !!partnerRef
    const hasBack = !!back
    const faceFront = front

    if (isPartner) {
      if (onlyFront || onlyPartner === 'first') return { cardId, front: faceFront }
      if (onlyBack || onlyPartner === 'second') return { cardId: partnerRef.cardId, front: frontPartner }
      if (reverse) return { back: faceFront, cardId, front: frontPartner, variant: 'partner' }
      return { back: frontPartner, cardId, front: faceFront, variant: 'partner' }
    }

    if (!hasBack) return { cardId, front: faceFront, variant: 'standard' }

    const variant = 'dfc'

    if (onlyFront) return { cardId, front: faceFront }
    if (onlyBack) return { cardId, front: back }
    if (reverse) return { back: faceFront, cardId, front: back, variant }
    return { back, cardId, front: faceFront, variant }
  }, [back, front, frontPartner, onlyBack, onlyFront, onlyPartner, partnerRef, reverse, cardId])

  return (
    <CardFaces
      {...facesProps}
      animate={animate}
      inViewStatus={inViewStatus}
      linkProps={linkProps}
      onClick={onClick}
      showPrices={showPrices}
    />
  )
}

export const CardRecFaces = memo(CardRecFacesInner)
